import "core-js/modules/es.array.push.js";
import moment from 'moment';
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { DoctorService } from '@/services/doctor.service';
import { REASSIGNMENT_TABLE_HEADERS_LIST, REASSIGNMENT_COLUMN_OPTIONS_LIST, REASSIGNMENT_STATUS_OPTIONS_LIST, REASSIGNMENT_STATE_OPTIONS_LIST } from '@/constants/reassignment';
import CoreTooltip from '@corefront/components-v2/Core/CoreTooltip.vue';
const TAB_TO_BACKEND = {
  scheduled: 'pending',
  active: 'active',
  cancelled: 'cancelled',
  all: 'all'
};
const REASON_CATEGORIES = ['License Expiration', 'Doctor Offboarding', 'Panel Rebalancing', 'Other'];
export default {
  components: {
    CoreTooltip
  },
  data() {
    return {
      headers: REASSIGNMENT_TABLE_HEADERS_LIST,
      items: [],
      total: 0,
      fetching: false,
      tabOptions: [{
        value: 'scheduled',
        text: 'Scheduled'
      }, {
        value: 'active',
        text: 'Active'
      }, {
        value: 'cancelled',
        text: 'Cancelled'
      }, {
        value: 'all',
        text: 'All'
      }],
      tabCounts: {
        scheduled: '—',
        active: '—',
        cancelled: '—',
        all: '—'
      },
      currentPage: parseInt(this.$route.query.page) || 1,
      search: this.$route.query.search || '',
      debouncedSearch: this.$route.query.search || '',
      searchDebouncer: null,
      filters: [],
      pendingFilters: [],
      fetchVersion: 0,
      columnOptions: REASSIGNMENT_COLUMN_OPTIONS_LIST,
      statusOptions: REASSIGNMENT_STATUS_OPTIONS_LIST,
      stateOptions: REASSIGNMENT_STATE_OPTIONS_LIST
    };
  },
  computed: {
    ...mapGetters('auth', ['isManager']),
    tab() {
      const valid = ['scheduled', 'active', 'cancelled', 'all'];
      return valid.includes(this.$route.query.tab) ? this.$route.query.tab : 'all';
    },
    tableHeightStyle() {
      const offset = 350 + (this.filters.length > 0 ? 44 : 0);
      const h = `calc(100vh - ${offset}px)`;
      return {
        maxHeight: `${h} !important`,
        height: `${h} !important`
      };
    }
  },
  watch: {
    search() {
      this.searchDebouncer();
    },
    async debouncedSearch() {
      this.currentPage = 1;
      await this.fetchData();
    },
    async tab() {
      this.currentPage = 1;
      await this.fetchData();
    }
  },
  async mounted() {
    this.searchDebouncer = _.debounce(() => {
      this.debouncedSearch = this.search;
      this.$router.replace({
        query: {
          ...this.$route.query,
          search: this.search.trim() || undefined,
          page: undefined
        }
      }).catch(() => {});
    }, 500);
    this.fetchTabCounts();
    await this.fetchData();
  },
  beforeDestroy() {
    if (this.searchDebouncer) {
      this.searchDebouncer.cancel();
    }
  },
  methods: {
    async fetchTabCounts() {
      const tabs = ['scheduled', 'active', 'cancelled', 'all'].filter(t => t !== this.tab);
      try {
        const results = await Promise.all(tabs.map(t => DoctorService.getPatientReassignments({
          tab: TAB_TO_BACKEND[t],
          limit: 1,
          offset: 0
        })));
        tabs.forEach((t, index) => {
          this.tabCounts[t] = results[index].data.pagination.total;
        });
      } catch (e) {
        console.error('Failed to fetch reassignment tab counts', e);
      }
    },
    async fetchData() {
      this.fetchVersion += 1;
      const version = this.fetchVersion;
      this.fetching = true;
      try {
        const offset = (this.currentPage - 1) * 25;
        const payload = {
          tab: TAB_TO_BACKEND[this.tab],
          limit: 25,
          offset,
          sortBy: 'startdatestamp',
          sortDir: 'desc'
        };
        if (this.debouncedSearch.trim()) {
          payload.search = this.debouncedSearch.trim();
        }
        if (this.filters.length) {
          payload.filters = this.filters;
        }
        const {
          data
        } = await DoctorService.getPatientReassignments(payload);
        if (version !== this.fetchVersion) {
          return;
        }
        this.tabCounts[this.tab] = data.pagination.total;
        this.items = data.data;
        this.total = data.pagination.total;
      } catch (e) {
        console.error('Failed to fetch patient reassignments', e);
      } finally {
        if (version === this.fetchVersion) {
          this.fetching = false;
        }
      }
    },
    // eslint-disable-next-line vue/no-unused-properties
    async refresh() {
      await this.fetchData();
      this.fetchTabCounts();
    },
    setReassignmentTab(newTab) {
      this.$router.push({
        query: {
          ...this.$route.query,
          tab: newTab,
          page: undefined
        }
      }).catch(() => {});
    },
    handlePaginationClick(_event, pageNumber) {
      this.currentPage = pageNumber;
      this.$router.replace({
        query: {
          ...this.$route.query,
          page: pageNumber > 1 ? pageNumber : undefined
        }
      }).catch(() => {});
      this.fetchData();
    },
    formatListingStatus(row) {
      const {
        requeststatus,
        reassignmentstatus,
        enddatestamp,
        startdatestamp
      } = row;
      if (!requeststatus) {
        return {
          status: '—',
          subStatus: null
        };
      }
      if (requeststatus === 'CANCELLED') {
        return {
          status: 'Cancelled',
          subStatus: null
        };
      }
      if (Number(startdatestamp) > moment().unix()) {
        return {
          status: 'Scheduled',
          subStatus: null
        };
      }
      if (this.deriveReassignmentType(row) === 'Temporary' && reassignmentstatus === 'PROCESSING' && Number(enddatestamp) < moment().unix()) {
        return {
          status: 'Active',
          subStatus: 'Return Ongoing'
        };
      }
      if (reassignmentstatus === 'RETURNED') {
        return {
          status: 'Completed',
          subStatus: 'Return Complete'
        };
      }
      if (reassignmentstatus === 'PROCESSING') {
        return {
          status: 'Active',
          subStatus: 'Reassignment Ongoing'
        };
      }
      if (reassignmentstatus === 'TRANSFERRED') {
        return {
          status: 'Active',
          subStatus: 'Reassignment Complete'
        };
      }
      return {
        status: requeststatus,
        subStatus: null
      };
    },
    deriveReassignmentType(row) {
      return row.enddatestamp ? 'Temporary' : 'Permanent';
    },
    formatRequestNotes(reason) {
      if (!reason || typeof reason !== 'string') {
        return '—';
      }
      const trimmed = reason.trim();
      const category = REASON_CATEGORIES.find(c => trimmed.startsWith(c));
      if (!category) {
        return trimmed;
      }
      const details = trimmed.slice(category.length).trim();
      const cleanDetails = details && details !== 'null' ? details : '';
      if (category === 'Other') {
        return cleanDetails ? `Other - ${cleanDetails}` : 'Other';
      }
      return category;
    },
    showEdit(row) {
      return this.formatListingStatus(row).status === 'Scheduled';
    },
    showCancel(row) {
      const {
        status
      } = this.formatListingStatus(row);
      return (status === 'Scheduled' || status === 'Active') && this.deriveReassignmentType(row) === 'Temporary';
    },
    isCancelReadonly(row) {
      const {
        status,
        subStatus
      } = this.formatListingStatus(row);
      return status === 'Active' && subStatus === 'Reassignment Ongoing' && this.deriveReassignmentType(row) === 'Temporary';
    },
    showMakePermanent(row) {
      const {
        status,
        subStatus
      } = this.formatListingStatus(row);
      return this.deriveReassignmentType(row) === 'Temporary' && status === 'Active' && subStatus === 'Reassignment Complete';
    },
    getDate(date) {
      if (!date) {
        return '—';
      }
      return moment(date * 1000).format('MM/DD/YYYY LT');
    },
    getFirstStates(states) {
      return states.slice(0, 5).map(s => s.state).join(', ');
    },
    getRemainingStates(states) {
      return states.slice(5).map(s => s.state).join(', ');
    },
    clearSearch() {
      this.search = '';
      this.$refs.searchInput.focus();
    },
    onFilterPanelShow() {
      this.pendingFilters = this.filters.map(filter => {
        if ((filter.column === 'startDate' || filter.column === 'endDate') && filter.value) {
          return {
            ...filter,
            value: moment.unix(filter.value).format('YYYY-MM-DD')
          };
        }
        return {
          ...filter
        };
      });
      if (this.pendingFilters.length === 0) {
        this.pendingFilters.push(this.newCondition());
      }
    },
    newCondition() {
      return {
        column: 'status',
        operator: 'eq',
        value: ''
      };
    },
    addCondition() {
      this.pendingFilters.push(this.newCondition());
    },
    removeCondition(index) {
      this.pendingFilters.splice(index, 1);
    },
    onColumnChange(index) {
      const col = this.pendingFilters[index].column;
      const [first] = this.getOperators(col);
      this.pendingFilters[index].operator = first.value;
      this.pendingFilters[index].value = '';
    },
    getOperators(column) {
      switch (column) {
        case 'status':
          return [{
            value: 'eq',
            text: 'is'
          }, {
            value: 'neq',
            text: 'is not'
          }];
        case 'state':
          return [{
            value: 'eq',
            text: 'is'
          }, {
            value: 'neq',
            text: 'is not'
          }];
        case 'startDate':
        case 'endDate':
          return [{
            value: 'gte',
            text: 'is after'
          }, {
            value: 'lte',
            text: 'is before'
          }];
        default:
          return [{
            value: 'contains',
            text: 'contains'
          }];
      }
    },
    applyFilters() {
      this.filters = this.pendingFilters.filter(cond => cond.value && String(cond.value).trim()).map(cond => {
        if (cond.column === 'startDate' || cond.column === 'endDate') {
          return {
            column: cond.column,
            operator: cond.operator,
            value: moment(cond.value, 'YYYY-MM-DD').unix()
          };
        }
        return {
          column: cond.column,
          operator: cond.operator,
          value: cond.value
        };
      });
      this.$refs.filterDropdown.hide();
      this.currentPage = 1;
      this.$router.replace({
        query: {
          ...this.$route.query,
          page: undefined
        }
      }).catch(() => {});
      this.fetchData();
    },
    cancelFilter() {
      this.$refs.filterDropdown.hide();
    },
    clearAllFilters() {
      this.filters = [];
      this.currentPage = 1;
      this.$router.replace({
        query: {
          ...this.$route.query,
          page: undefined
        }
      }).catch(() => {});
      this.fetchData();
    },
    removeFilter(index) {
      this.filters.splice(index, 1);
      this.currentPage = 1;
      this.$router.replace({
        query: {
          ...this.$route.query,
          page: undefined
        }
      }).catch(() => {});
      this.fetchData();
    },
    formatFilterChip(filter) {
      var _this$getOperators$fi;
      const colLabel = (this.columnOptions.find(option => option.value === filter.column) || {}).text || filter.column;
      const opLabel = ((_this$getOperators$fi = this.getOperators(filter.column).find(option => option.value === filter.operator)) === null || _this$getOperators$fi === void 0 ? void 0 : _this$getOperators$fi.text) || filter.operator;
      let val;
      if (filter.column === 'status') {
        val = (this.statusOptions.find(option => option.value === filter.value) || {}).text || filter.value;
      } else if (filter.column === 'state') {
        val = (this.stateOptions.find(option => option.value === filter.value) || {}).text || filter.value;
      } else if (filter.column === 'startDate' || filter.column === 'endDate') {
        val = moment.unix(filter.value).format('MMM D, YYYY');
      } else {
        val = filter.value;
      }
      return `${colLabel} ${opLabel}: ${val}`;
    }
  }
};